import { useEffect, useRef, useState } from 'react'

type Props = {
  /**
   * Margin around the root element. This margin is used to extend or shrink the viewport for the intersection check.
   * @default "0px"
   */
  rootMargin?: string
  /**
   * A number between 0 and 1 that specifies the percentage of the target's visibility
   * before the observer's callback is executed. For example, 0.1 means the observer will
   * trigger when 10% of the target is visible.
   * @default 0.1
   */
  threshold?: number
  /**
   * The element to use as the viewport for checking visibility of the target.
   * If null, the browser's viewport is used.
   * @default null
   */
  root?: Element | Document | null
}

type Return = {
  /**
   * A React ref to be attached to the DOM element that should be observed for visibility.
   */
  ref: React.MutableRefObject<HTMLDivElement | null>
  /**
   * A boolean indicating whether the element is visible in the viewport.
   */
  isVisible: boolean
}

/**
 * A custom hook that triggers a lazy load when an element becomes visible within the viewport.
 * It uses the IntersectionObserver API to observe visibility and sets the `isVisible` state
 * when the observed element enters the viewport.
 *
 * @param {Props} props - The configuration object for the hook.
 *
 * @returns {Return} - An object containing a `ref` to attach to the observed element and
 * the `isVisible` boolean state to indicate if the element is visible in the viewport.
 *
 * @example
 * // Usage example:
 * const { ref, isVisible } = useIntersectionObserver({ rootMargin: '100px', threshold: 0.2 });
 *
 * return (
 *   <div ref={ref}>
 *     {isVisible ? <Content /> : <Placeholder />}
 *   </div>
 * );
 */
export const useIntersectionObserver = ({
  rootMargin,
  threshold = 0.1,
  root = null,
}: Props): Return => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(ref.current)
      }
    }
  }, [root, rootMargin, threshold])

  return { ref, isVisible }
}
